"use client";
import React, { useState, useEffect } from "react";
import Logo from "@/components/ratl/utility/logo-nav";
import { RT_LOGO_BLACK_URL, RT_LOGO_CTA } from "@/data";
import NavbarLinks from "./NavbarLinks";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { ArrowUpRight } from "lucide-react";

const HamburgerIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16m-7 6h7"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const NavbarMobile: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setIsMounted(true);
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isMounted) return;
    
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, isMounted]);

  return (
    <>
      <div className="lg:hidden fixed p-1 rounded-xl border-[#c8c8c8]/25 border border-solid bg-white/80 backdrop-filter backdrop-blur-[25px] top-5 left-5 right-5 z-[9999]">
        <div className="flex flex-row items-center justify-between h-full px-2">
          <a href={RT_LOGO_CTA} aria-label="Home">
            <Logo logoSrc={RT_LOGO_BLACK_URL} width={100} />
          </a>
          <button onClick={() => setIsOpen(!isOpen)} className="p-2 z-50">
            <HamburgerIcon />
          </button>
        </div>
      </div>

      {isMounted && (
        <>
          <div
            className={`lg:hidden fixed inset-0 bg-black/50 z-[9998] transition-opacity duration-300 ${
              isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            onClick={() => setIsOpen(false)}
          />

          <div
            className={`lg:hidden fixed top-0 right-0 h-full w-4/5 max-w-sm bg-white z-[9999] shadow-lg transform transition-transform duration-300 ease-in-out flex flex-col overflow-y-auto max-h-screen ${
              isOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
        <div className="flex items-center justify-end p-4 border-b border-gray-200">
          <button onClick={() => setIsOpen(false)} className="p-2">
            <CloseIcon />
          </button>
        </div>
        <div className="p-5 flex-grow">
          <NavbarLinks />
          {/* Customer Stories section header */}
          <div className="mt-8">
            <div className="text-lg font-semibold text-gray-800 mb-3 pl-1">Customer Stories</div>
            <div
              className="bg-white hover:bg-gray-100 md:py-0 h-[340px] w-full md:h-auto max-w-[900px] flex flex-col md:flex-row rounded-xl cursor-pointer border border-[#D6CBC2]
              shadow-[0px_1px_2px_0px_rgba(95,74,46,0.08),0px_4px_6px_0px_rgba(95,74,46,0.04),0px_24px_40px_-16px_rgba(104,75,37,0.08)]
              md:border-0 md:shadow-[0px_4px_6px_0px_rgba(95,74,46,0.04)] md:hover:shadow-[0px_1px_2px_0px_rgba(95,74,46,0.08),0px_4px_6px_0px_rgba(95,74,46,0.04),0px_24px_40px_-16px_rgba(104,75,37,0.08)] justify-between"
              onClick={() => {
                setIsOpen(false);
                window.location.href = "/customers/commerce";
              }}
            >
              <div className="relative order-1 md:order-2 flex justify-center items-center">
                <Image
                  src="/ratl/customer_stories/reliance_icon.png"
                  alt="Reliance Jio"
                  width={400}
                  height={320}
                  className="w-[140px] h-[110px] md:w-[400px] md:h-[320px] object-contain rounded-lg absolute top-0 right-0 md:static"
                  priority
                />
              </div>
              <div className="order-2 md:order-1 flex flex-col justify-center items-start gap-4 md:w-[340px] md:ml-[32px] p-6 md:p-0">
                <Image
                  src="/ratl/customer_stories/reliance_logo.png"
                  alt="Reliance Jio"
                  width={100}
                  height={50}
                  className="w-1/3 md:w-[100px] md:h-[50px] object-contain md:block"
                />
                <div className="flex flex-col gap-6 md:gap-8 mt-[10px] md:mt-0">
                  <h1 className="md:text-[24px] text-[18px] leading-[130%] tracking-[0.28px] font-medium font-inter text-light-main-heading">
                    Retail & <br className="md:hidden" /> E-Commerce: <br className="md:hidden" /> Reliance Jio
                  </h1>
                  <p className="font-inter text-light-body-text text-[15px] md:text-[17px] leading-[130%] tracking-[0.12px] font-normal">
                    A transformative journey in <br /> achieving AI-Driven reliability
                  </p>
                  <span className="flex font-inter items-center text-light-body-text font-normal text-[13px] md:text-[17px] leading-[130%] tracking-[0.5px]">
                    <span>Read story</span> <div><ArrowUpRight className="w-5 h-5 md:w-6 md:h-6" /></div>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-gray-200">
          <a
            href={RT_LOGO_CTA}
            aria-label="Home"
            onClick={() => setIsOpen(false)}
          >
            <Logo logoSrc={RT_LOGO_BLACK_URL} width={100} />
          </a>
        </div>
      </div>
        </>
      )}
    </>
  );
};

export default NavbarMobile;
